// Typed money and percent inputs: mark the input with `data-decimal-input`
// instead of an inline @input regex. The filter never decides what is a valid
// number and never drops a character the operator could mean: letters, "-",
// currency and percent signs all reach the server as typed, and the server's
// parser (numfmt.Parse) and the domain either read them correctly or refuse
// them with a field error. Silently stripping them used to turn "abc" into an
// empty (valid) field and "-5" into 5. "7,5", "89,50" and "1.250,50" also
// reach the server as typed, so a value can never change magnitude on the way.
//
// The only thing dropped is whitespace the server cannot read inside a number:
// ASCII space, NBSP and narrow NBSP are the grouping spaces numfmt.Parse
// accepts and are kept; tabs, newlines and other Unicode spaces are dropped.
// A value of data-decimal-input="signed" is accepted and behaves like the bare
// attribute, because a minus is no longer filtered for any field.
const UNACCEPTED_WHITESPACE = /[^\S \u00A0\u202F]/g;

export function sanitizeDecimalInput(value: string): string {
    return value.replace(UNACCEPTED_WHITESPACE, "");
}

// caretAfterSanitize maps a caret position in value to the same spot in the
// sanitized value: right after the kept characters that preceded it.
export function caretAfterSanitize(value: string, caret: number): number {
    return sanitizeDecimalInput(value.slice(0, caret)).length;
}

// sanitizeField cleans el in place, keeping the caret (or selection) where the
// operator left it rather than letting the assignment jump it to the end. It
// reports whether the value changed.
function sanitizeField(el: HTMLInputElement): boolean {
    const raw = el.value;
    const clean = sanitizeDecimalInput(raw);
    if (clean === raw) return false;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    el.value = clean;
    if (start !== null && end !== null && el.ownerDocument.activeElement === el) {
        el.setSelectionRange(
            Math.min(caretAfterSanitize(raw, start), clean.length),
            Math.min(caretAfterSanitize(raw, end), clean.length),
        );
    }
    return true;
}

function decimalInputTarget(event: Event): HTMLInputElement | null {
    const el = event.target;
    if (!(el instanceof HTMLInputElement) || !el.hasAttribute("data-decimal-input")) return null;
    return el;
}

// registerDecimalInput installs one delegated listener for every current and
// future `[data-decimal-input]` input, so HTMX-swapped forms need no init. It
// listens in the capture phase so the value is already clean when the
// input's own handlers (x-model, @input) see the event.
export function registerDecimalInput(root: Document): void {
    root.addEventListener(
        "input",
        (event: Event) => {
            const el = decimalInputTarget(event);
            if (!el) return;
            // Text mid-composition is provisional; rewriting it would cancel
            // the IME. compositionend cleans the committed text instead.
            if (event instanceof InputEvent && event.isComposing) return;
            sanitizeField(el);
        },
        true,
    );
    root.addEventListener(
        "compositionend",
        (event: Event) => {
            const el = decimalInputTarget(event);
            // Some browsers fire no input event after compositionend, so tell
            // x-model/@input about the cleaned value; the re-entrant input
            // event finds nothing left to clean.
            if (el && sanitizeField(el)) el.dispatchEvent(new Event("input", { bubbles: true }));
        },
        true,
    );
}
